import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {getPriceAmendmentDefaults, createPriceAmendments} from '../api/contractApi';
import {formatCurrency} from '../utils/currency';

// Same Quill configuration as SendMessage.js
const QUILL_MODULES = {
    toolbar: [
        [{'header': [1, 2, 3, false]}],
        ['bold', 'italic', 'underline', 'strike'],
        [{'color': []}, {'background': []}],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        [{'align': []}],
        ['link'],
        ['clean']
    ],
};

const QUILL_FORMATS = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link'
];

const LEGAL_POINTS = [
    'Under Estonian law (Law of Obligations Act § 13 and § 42(3) 14–15) and EU Directive 93/13/EEC on unfair contract terms, a unilateral price change in a consumer contract is only valid if the contract contains a price-change clause with a valid reason, and the member has been notified in advance (in practice at least 30 days) on a durable medium such as e-mail.',
    'The member must have the right to terminate the contract before the new price applies, free of charge.',
    'This tool changes the price IMMEDIATELY and sends an e-mail notification now. It does not replace the advance notice – make sure you have already informed these members before confirming.',
    'Every change is recorded (old price, new price, reason, who, when and the e-mail content) and is visible to the member under Profile -> Contracts.',
    'Recommendation: include a price-change clause in your default contract template (Add Default Contract).'
];

const roundMoney = (value) => Math.round(Number(value) * 100) / 100;

// Compact clear (x) and dropdown arrow buttons inside Autocomplete inputs
const AUTOCOMPLETE_SX = {
    '& .MuiAutocomplete-endAdornment': {
        top: '50%',
        transform: 'translateY(-50%)',
        right: 8
    },
    '& .MuiAutocomplete-endAdornment .MuiIconButton-root': {
        width: 28,
        height: 28,
        padding: 0.25,
        border: 'none',
        backgroundColor: 'transparent',
        boxShadow: 'none'
    },
    '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
        fontSize: 18
    }
};

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderTemplate = (template, vars, escape) => (template || '').replace(
    /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
    (match, key) => {
        if (!Object.prototype.hasOwnProperty.call(vars, key)) return match;
        return escape ? escapeHtml(vars[key]) : String(vars[key] ?? '');
    }
);

export default function ContractPriceChangeDialog({
    open,
    affiliateId,
    affiliateName,
    affiliateEmail,
    contracts,
    currency = 'EUR',
    onClose,
    onDone
}) {
    // Filters
    const [selectedPrices, setSelectedPrices] = useState([]);
    const [rangeMin, setRangeMin] = useState('');
    const [rangeMax, setRangeMax] = useState('');

    // Manual adjustments to the filtered selection
    const [removedIds, setRemovedIds] = useState(() => new Set());
    const [addedIds, setAddedIds] = useState(() => new Set());
    const [manualAddValue, setManualAddValue] = useState(null);
    const [manualAddInput, setManualAddInput] = useState('');

    // New price
    const [priceMode, setPriceMode] = useState('fixed');
    const [priceValue, setPriceValue] = useState('');

    // Reason + e-mail
    const [reason, setReason] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [saveAsTemplate, setSaveAsTemplate] = useState(false);
    const [placeholders, setPlaceholders] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Confirmation
    const [acknowledged, setAcknowledged] = useState(false);

    // Loading states
    const [loadingDefaults, setLoadingDefaults] = useState(false);
    const [defaultsError, setDefaultsError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const formatMoney = (value) => formatCurrency(value, currency);

    const eligibleContracts = useMemo(
        () => (contracts || []).filter(
            (c) => c.status === 'accepted' && c.active !== false && c.paymentAmount !== null && c.paymentAmount !== undefined
        ),
        [contracts]
    );

    const priceOptions = useMemo(() => {
        const set = new Set(eligibleContracts.map((c) => roundMoney(c.paymentAmount)));
        return Array.from(set).sort((a, b) => a - b);
    }, [eligibleContracts]);

    const loadDefaults = async () => {
        if (!affiliateId) return;
        setLoadingDefaults(true);
        setDefaultsError('');
        try {
            const data = await getPriceAmendmentDefaults(affiliateId);
            setEmailSubject(data.subject || '');
            setEmailBody(data.body || '');
            setPlaceholders(data.placeholders || []);
        } catch (error) {
            console.error('Error loading price change defaults:', error);
            setDefaultsError(error?.message || 'Failed to load e-mail template.');
        } finally {
            setLoadingDefaults(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        // Reset state on each open
        setSelectedPrices([]);
        setRangeMin('');
        setRangeMax('');
        setRemovedIds(new Set());
        setAddedIds(new Set());
        setManualAddValue(null);
        setManualAddInput('');
        setPriceMode('fixed');
        setPriceValue('');
        setReason('');
        setSaveAsTemplate(false);
        setAcknowledged(false);
        setSubmitError('');
        setPreviewOpen(false);
        loadDefaults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, affiliateId]);

    // Contracts matched by filters (OR)
    const filterMatchedIds = useMemo(() => {
        const prices = new Set(selectedPrices.map((p) => roundMoney(p)));
        const min = rangeMin === '' ? null : Number(rangeMin);
        const max = rangeMax === '' ? null : Number(rangeMax);
        const hasRange = (min !== null && !Number.isNaN(min)) || (max !== null && !Number.isNaN(max));

        const ids = new Set();
        eligibleContracts.forEach((c) => {
            const amount = roundMoney(c.paymentAmount);
            if (prices.has(amount)) ids.add(c.id);
            if (hasRange) {
                const aboveMin = min === null || Number.isNaN(min) || amount >= min;
                const belowMax = max === null || Number.isNaN(max) || amount <= max;
                if (aboveMin && belowMax) ids.add(c.id);
            }
        });
        return ids;
    }, [eligibleContracts, selectedPrices, rangeMin, rangeMax]);

    const computeNewPrice = (current) => {
        const value = Number(priceValue);
        if (priceValue === '' || Number.isNaN(value)) return null;
        const base = Number(current);
        switch (priceMode) {
            case 'amount':
                return roundMoney(base + value);
            case 'percent':
                return roundMoney(base * (1 + value / 100));
            case 'fixed':
            default:
                return roundMoney(value);
        }
    };

    const affectedRows = useMemo(() => {
        const ids = new Set([...filterMatchedIds, ...addedIds]);
        removedIds.forEach((id) => ids.delete(id));
        return eligibleContracts
            .filter((c) => ids.has(c.id))
            .map((c) => {
                const newPrice = computeNewPrice(c.paymentAmount);
                let error = '';
                if (newPrice === null) error = 'Enter a price';
                else if (newPrice <= 0) error = 'Price must be above 0';
                else if (roundMoney(c.paymentAmount) === newPrice) error = 'Same as current price';
                return {contract: c, newPrice, error};
            })
            .sort((a, b) => (a.contract.user?.fullName || '').localeCompare(b.contract.user?.fullName || ''));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eligibleContracts, filterMatchedIds, addedIds, removedIds, priceMode, priceValue]);

    const affectedIdSet = useMemo(() => new Set(affectedRows.map((r) => r.contract.id)), [affectedRows]);

    const manualAddOptions = useMemo(
        () => eligibleContracts.filter((c) => !affectedIdSet.has(c.id)),
        [eligibleContracts, affectedIdSet]
    );

    const handleRemoveRow = (contractId) => {
        setRemovedIds((prev) => new Set([...prev, contractId]));
        setAddedIds((prev) => {
            const next = new Set(prev);
            next.delete(contractId);
            return next;
        });
    };

    const handleManualAdd = (contract) => {
        if (!contract) return;
        setAddedIds((prev) => new Set([...prev, contract.id]));
        setRemovedIds((prev) => {
            const next = new Set(prev);
            next.delete(contract.id);
            return next;
        });
        setManualAddValue(null);
        setManualAddInput('');
    };

    const MANUAL_SEARCH_MIN_CHARS = 3;
    const MANUAL_SEARCH_LIMIT = 10;

    // Display: "Name – Contract type – Price"; for family member contracts
    // "Child name – Parent name (parent) – Contract type – Price"
    const contractDisplayName = (c) => {
        const parentName = c.user?.fullName || `User ${c.userId}`;
        if (c.isFamilyMemberContract && c.familyMember?.fullName) {
            return `${c.familyMember.fullName} – ${parentName} (parent)`;
        }
        return parentName;
    };

    const contractLabel = (c) => [
        contractDisplayName(c),
        c.contractType || 'Contract',
        formatMoney(c.paymentAmount)
    ].join(' – ');

    const manualSearchTerm = manualAddInput.trim().toLowerCase();
    const manualSearchActive = manualSearchTerm.length >= MANUAL_SEARCH_MIN_CHARS;

    // Local name search (member or family member name), min 3 chars, max 10 results
    const filterManualAddOptions = (options) => {
        if (!manualSearchActive) return [];
        return options
            .filter((c) => {
                const parentName = (c.user?.fullName || '').toLowerCase();
                const childName = (c.familyMember?.fullName || '').toLowerCase();
                return parentName.includes(manualSearchTerm) || childName.includes(manualSearchTerm);
            })
            .slice(0, MANUAL_SEARCH_LIMIT);
    };

    const hasRowErrors = affectedRows.some((r) => r.error);
    const canSubmit = acknowledged
        && affectedRows.length > 0
        && !hasRowErrors
        && reason.trim().length > 0
        && emailSubject.trim().length > 0
        && emailBody.trim().length > 0
        && !loadingDefaults
        && !submitting;

    const previewVars = useMemo(() => {
        const first = affectedRows[0];
        const userName = first?.contract.user?.fullName || 'Member Name';
        const familyMemberName = first?.contract.isFamilyMemberContract
            ? (first.contract.familyMember?.fullName || '')
            : '';
        return {
            userName,
            familyMemberName,
            contractHolder: familyMemberName || userName,
            affiliateName: affiliateName || 'Your club',
            affiliateEmail: affiliateEmail || 'info@irontrack.ee',
            oldPrice: first ? Number(first.contract.paymentAmount).toFixed(2) : '0.00',
            newPrice: first && first.newPrice !== null ? first.newPrice.toFixed(2) : '0.00',
            currency,
            reason: reason || '…',
            changeDate: new Date().toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'}),
            contractType: first?.contract.contractType || 'Membership contract'
        };
    }, [affectedRows, affiliateName, affiliateEmail, currency, reason]);

    const handleClose = () => {
        if (submitting) return;
        onClose?.();
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setSubmitError('');
        try {
            const result = await createPriceAmendments({
                affiliateId,
                items: affectedRows.map((r) => ({contractId: r.contract.id, newAmount: r.newPrice})),
                reason: reason.trim(),
                emailSubject: emailSubject.trim(),
                emailBody: emailBody.trim(),
                saveAsTemplate,
                acknowledged: true
            });
            onDone?.(result);
        } catch (error) {
            console.error('Error changing contract prices:', error);
            setSubmitError(error?.message || 'Failed to change contract prices.');
        } finally {
            setSubmitting(false);
        }
    };

    const inputsDisabled = submitting;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth disableEscapeKeyDown={submitting}>
            <DialogTitle>Change Contract Prices</DialogTitle>
            <DialogContent dividers>
                {/* Legal information */}
                <Alert severity="warning" sx={{mb: 3}}>
                    <AlertTitle>Before you change prices – please read</AlertTitle>
                    <Box component="ol" sx={{pl: 2.5, m: 0}}>
                        {LEGAL_POINTS.map((point, index) => (
                            <li key={index}>
                                <Typography variant="body2">{point}</Typography>
                            </li>
                        ))}
                    </Box>
                </Alert>

                {/* Selection */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    1. Select contracts
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Only active, accepted contracts with a price can be changed. Pick exact prices and/or a price
                    range; a contract is included if it matches either. You can remove or add individual contracts below.
                </Typography>
                <Stack spacing={3} sx={{mb: 4}}>
                    <Box>
                        <Autocomplete
                            multiple
                            options={priceOptions}
                            value={selectedPrices}
                            onChange={(e, value) => setSelectedPrices(value)}
                            getOptionLabel={(option) => formatMoney(option)}
                            isOptionEqualToValue={(option, value) => roundMoney(option) === roundMoney(value)}
                            disabled={inputsDisabled}
                            disableCloseOnSelect
                            renderTags={() => null}
                            sx={AUTOCOMPLETE_SX}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Contracts with a specific price"
                                    placeholder={selectedPrices.length ? 'Add another price...' : 'Select prices...'}
                                    helperText="All contracts that currently have exactly this price are included."
                                />
                            )}
                        />
                        {selectedPrices.length > 0 && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mt: 1}}>
                                {selectedPrices.map((price) => (
                                    <Chip
                                        key={price}
                                        label={formatMoney(price)}
                                        size="small"
                                        onDelete={inputsDisabled
                                            ? undefined
                                            : () => setSelectedPrices((prev) => prev.filter((p) => roundMoney(p) !== roundMoney(price)))}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <TextField
                            label={`Price range from (${currency})`}
                            type="number"
                            value={rangeMin}
                            onChange={(e) => setRangeMin(e.target.value)}
                            inputProps={{min: 0, step: '0.01'}}
                            disabled={inputsDisabled}
                            fullWidth
                        />
                        <TextField
                            label={`Price range to (${currency})`}
                            type="number"
                            value={rangeMax}
                            onChange={(e) => setRangeMax(e.target.value)}
                            inputProps={{min: 0, step: '0.01'}}
                            disabled={inputsDisabled}
                            fullWidth
                        />
                    </Stack>
                </Stack>

                {/* New price */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    2. New price
                </Typography>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} sx={{mb: 4}}>
                    <FormControl sx={{minWidth: 260}} disabled={inputsDisabled}>
                        <InputLabel id="price-mode-label">Mode</InputLabel>
                        <Select
                            labelId="price-mode-label"
                            value={priceMode}
                            label="Mode"
                            onChange={(e) => setPriceMode(e.target.value)}
                        >
                            <MenuItem value="fixed">Set fixed price</MenuItem>
                            <MenuItem value="amount">Change by amount (+/-)</MenuItem>
                            <MenuItem value="percent">Change by percent (+/-)</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label={priceMode === 'fixed'
                            ? `New price (${currency})`
                            : priceMode === 'amount'
                                ? `Change amount (${currency}, negative to decrease)`
                                : 'Change percent (negative to decrease)'}
                        type="number"
                        value={priceValue}
                        onChange={(e) => setPriceValue(e.target.value)}
                        inputProps={{step: '0.01'}}
                        disabled={inputsDisabled}
                        fullWidth
                    />
                </Stack>

                {/* Affected contracts */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    3. Affected contracts ({affectedRows.length} selected)
                </Typography>
                <Paper variant="outlined" sx={{mb: 2, maxHeight: 320, overflow: 'auto'}}>
                    {affectedRows.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{p: 2, textAlign: 'center'}}>
                            No contracts selected. Use the filters above or add contracts manually.
                        </Typography>
                    ) : (
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>Member</TableCell>
                                    <TableCell sx={{fontWeight: 'bold'}}>Contract type</TableCell>
                                    <TableCell sx={{fontWeight: 'bold'}} align="right">Current price</TableCell>
                                    <TableCell sx={{fontWeight: 'bold'}} align="right">New price</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {affectedRows.map(({contract, newPrice, error}) => (
                                    <TableRow key={contract.id} sx={error ? {bgcolor: '#fff4f4'} : undefined}>
                                        <TableCell>{contractDisplayName(contract)}</TableCell>
                                        <TableCell>{contract.contractType || '—'}</TableCell>
                                        <TableCell align="right">{formatMoney(contract.paymentAmount)}</TableCell>
                                        <TableCell align="right">
                                            {newPrice !== null ? formatMoney(newPrice) : '—'}
                                            {error && (
                                                <Typography variant="caption" display="block" color="error">
                                                    {error}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveRow(contract.id)}
                                                disabled={inputsDisabled}
                                                aria-label="Remove contract"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Paper>
                <Autocomplete
                    options={manualAddOptions}
                    value={manualAddValue}
                    inputValue={manualAddInput}
                    onInputChange={(e, value, reason) => {
                        if (reason === 'reset') {
                            setManualAddInput('');
                        } else {
                            setManualAddInput(value);
                        }
                    }}
                    onChange={(e, value) => handleManualAdd(value)}
                    open={manualSearchActive}
                    filterOptions={filterManualAddOptions}
                    getOptionLabel={contractLabel}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="No active, accepted contracts with a price match this name (inactive or pending contracts are not listed)"
                    forcePopupIcon={false}
                    clearOnBlur
                    blurOnSelect
                    disabled={inputsDisabled}
                    sx={{...AUTOCOMPLETE_SX, mb: 4}}
                    renderOption={(props, option) => {
                        const {key, ...optionProps} = props;
                        return (
                        <Box component="li" key={key ?? option.id} {...optionProps}>
                            <Box>
                                <Typography variant="body2">
                                    {contractDisplayName(option)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option.contractType || 'Contract'} – {formatMoney(option.paymentAmount)}
                                </Typography>
                            </Box>
                        </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Add contract manually"
                            placeholder="Type at least 3 letters of a member or family member name..."
                            helperText={manualSearchActive
                                ? `Showing up to ${MANUAL_SEARCH_LIMIT} matches`
                                : `Type at least ${MANUAL_SEARCH_MIN_CHARS} letters to search by name`}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <AddIcon fontSize="small" sx={{ml: 1, mr: 0.5, color: 'text.secondary'}} />
                                        {params.InputProps.startAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />

                {/* Reason */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    4. Reason
                </Typography>
                <TextField
                    label="Reason for the price change"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={inputsDisabled}
                    helperText="Included in the e-mail and stored in the contract log."
                    sx={{mb: 3}}
                />

                {/* E-mail */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    5. Notification e-mail
                </Typography>
                {loadingDefaults ? (
                    <Box display="flex" alignItems="center" gap={1.5} sx={{py: 2}}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading e-mail template...</Typography>
                    </Box>
                ) : defaultsError ? (
                    <Alert
                        severity="error"
                        sx={{mb: 2}}
                        action={<Button color="inherit" size="small" onClick={loadDefaults}>Retry</Button>}
                    >
                        {defaultsError}
                    </Alert>
                ) : null}
                <Stack spacing={2} sx={{mb: 3}}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Available placeholders:</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mt: 0.5}}>
                            {placeholders.map((p) => (
                                <Chip key={p.key} label={`{{${p.key}}}`} size="small" title={p.description} />
                            ))}
                        </Stack>
                    </Box>
                    <TextField
                        label="Subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        fullWidth
                        disabled={inputsDisabled || loadingDefaults}
                    />
                    <Box>
                        <Typography variant="body2" color="textSecondary" sx={{mb: 1}}>
                            Message Body
                        </Typography>
                        <div style={{border: '1px solid #ddd', borderRadius: '4px', marginBottom: '16px'}}>
                            <ReactQuill
                                theme="snow"
                                value={emailBody}
                                onChange={setEmailBody}
                                modules={QUILL_MODULES}
                                formats={QUILL_FORMATS}
                                readOnly={inputsDisabled || loadingDefaults}
                                style={{height: '250px', marginBottom: '40px'}}
                            />
                        </div>
                    </Box>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems={{sm: 'center'}} justifyContent="space-between">
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={saveAsTemplate}
                                    onChange={(e) => setSaveAsTemplate(e.target.checked)}
                                    disabled={inputsDisabled}
                                />
                            )}
                            label="Save as club default template"
                        />
                        <Button
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => setPreviewOpen(true)}
                            disabled={inputsDisabled || loadingDefaults}
                        >
                            Preview
                        </Button>
                    </Stack>
                </Stack>

                <Divider sx={{mb: 2}} />

                {/* Confirmation */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    6. Confirm
                </Typography>
                <Typography variant="body2" sx={{mb: 1}}>
                    <strong>{affectedRows.length}</strong> contract{affectedRows.length === 1 ? '' : 's'} will be updated
                    immediately and <strong>{affectedRows.length}</strong> notification e-mail{affectedRows.length === 1 ? '' : 's'} will
                    be sent in the background (delivery status is shown under "Previous Changes").
                </Typography>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={acknowledged}
                            onChange={(e) => setAcknowledged(e.target.checked)}
                            disabled={inputsDisabled}
                        />
                    )}
                    label="I confirm that these members have been informed in advance of this price change in accordance with the contract and applicable consumer law, and I understand that the new price applies immediately."
                />
                {submitError && (
                    <Alert severity="error" sx={{mt: 2}}>{submitError}</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                >
                    {submitting ? 'Applying...' : 'Confirm'}
                </Button>
            </DialogActions>

            {/* E-mail preview */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>E-mail preview</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="caption" color="text.secondary">Subject</Typography>
                    <Typography variant="subtitle1" sx={{mb: 2}}>
                        {renderTemplate(emailSubject, previewVars, false)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Body</Typography>
                    <Box
                        sx={{border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mt: 0.5}}
                        dangerouslySetInnerHTML={{__html: renderTemplate(emailBody, previewVars, true)}}
                    />
                    {affectedRows.length === 0 && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1}}>
                            Preview uses sample values until a contract is selected.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
